/**
 * Servizio per la gestione dello stato globale dell'applicazione.
 * Utilizza NgRx Signals per gestire lo stato e sincronizzarlo con il localStorage.
 * Gestisce autenticazione, profili utente (ora integrati nell'utente), premi e indirizzi.
 *
 * La chiave di localStorage è l'origin dell'app (es. https://app.primonetwork.it),
 * in stile http://accounts.gmail.com, per isolare i dati per dominio.
 */
import { withStorageSync } from '@angular-architects/ngrx-toolkit';
import { computed, inject } from '@angular/core';
import { IToken } from '@models/auth/auth.model';
import { IndirizzoDomicilio, IndirizzoResidenza, IndirizzoSpedizione } from '@models/indirizzo.model';
import { IPremio } from '@models/premio.model';
import { IProfilo, RuoloAttivo, User } from '@models/user.model';
import { patchState, signalStore, withComputed, withHooks, withMethods, withState } from '@ngrx/signals';
import { DeviceIdService } from '@services/device-id.service';
import { Console } from '@shared/providers/custom-logger';
import { environment } from 'src/environments/environment';

/** Chiave localStorage derivata dall'URL dell'app (origin). */
const APP_STORAGE_KEY =
  (typeof window !== 'undefined' && window.location?.origin)
    ? window.location.origin
    : 'app';

/** Chiave legacy usata prima della migrazione a APP_STORAGE_KEY. Usata solo per migrazione una tantum. */
const LEGACY_STORAGE_KEY = 'appDB';

/**
 * Interfaccia che definisce la struttura dello stato globale dell'applicazione.
 *
 * Contiene tutti i dati necessari per la gestione di:
 * - autenticazione
 * - profili utente (integrati nell'utente)
 * - premi
 * - indirizzi
 */
export interface IAuthStore {
    /** Token di autenticazione dell'utente */
    token: IToken | null;
    /** Dati dell'utente attualmente autenticato */
    user: User | null;
    /** Username dell'utente loggato */
    username: string | null;
    /** Eventuali errori dell'applicazione */
    error: any;
    /** Stato di caricamento dell'applicazione */
    isLoading: boolean;
    /** Identificativo univoco del dispositivo */
    device_id: string | null;
    /** URL base dell'app di autenticazione SSO */
    authAppUrl: string | null;
    /** ID profilo selezionato per multiutenza SSO */
    id_profilo: number | null;
    /** Flag che indica se l'utente ha già visto l'intro */
    hasSeenIntro: boolean;
    /** Ruolo attualmente attivo per l'utente */
    activeRole: RuoloAttivo | null;
    /** Lista dei premi disponibili */
    premi: IPremio[];
    /** Indirizzo di spedizione dell'utente */
    indirizzoSpedizione: IndirizzoSpedizione | null;
    /** Indirizzo di residenza dell'utente */
    indirizzoResidenza: IndirizzoResidenza | null;
    /** Indirizzo di domicilio dell'utente */
    indirizzoDomicilio: IndirizzoDomicilio | null;
    /**
     * Sessione ottenuta tramite SSO (Auth-App + ticket). Se false, login legacy con refresh token interno.
     * null = non autenticato o stato non ancora impostato dopo migrazione storage.
     */
    is_sso: boolean | null;
}

/**
 * Tipo interno che rappresenta la struttura dello store.
 *
 * @private
 */
type AuthStore = IAuthStore;

/**
 * Stato iniziale dello store dell'applicazione.
 *
 * Tutti i valori sono impostati a null o array vuoti per garantire
 * un avvio pulito dell'applicazione.
 */
const initialState: AuthStore = {
    token: null,
    user: null,
    username: null,
    error: null,
    isLoading: false,
    device_id: null,
    authAppUrl: null,
    id_profilo: null,
    hasSeenIntro: false,
    activeRole: null,
    premi: [],
    indirizzoSpedizione: null,
    indirizzoResidenza: null,
    indirizzoDomicilio: null,
    is_sso: null,
};

/**
 * Interfaccia che definisce i metodi disponibili per la gestione dello store.
 *
 * Questa interfaccia descrive tutti i metodi che possono essere utilizzati
 * per manipolare lo stato dell'applicazione.
 */
export interface IAppStoreMethods {
    /** Imposta il token di autenticazione */
    setToken: (token: IToken | null) => void;
    /** Imposta i dati dell'utente */
    setUser: (user: User) => void;
    /** Imposta lo username */
    setUsername: (username: string) => void;
    /** Imposta l'ID del dispositivo */
    setDeviceId: (deviceId: string) => void;
    /** Inizializza il device ID se non presente */
    initializeDeviceId: () => void;
    /** Imposta l'URL base dell'app di autenticazione SSO */
    setAuthAppUrl: (url: string) => void;
    /** Restituisce l'URL Auth-App risolto (da store o environment) */
    authAppUrlResolved: () => string;
    /** Imposta l'ID profilo per multiutenza SSO */
    setIdProfilo: (id: number | null) => void;
    /** Imposta se la sessione corrente proviene da SSO (true) o da login legacy (false) */
    setIsSso: (value: boolean | null) => void;
    /** Pulisce tutti i dati di autenticazione */
    clearAuth: () => void;
    /** Imposta lo stato di visualizzazione dell'intro */
    setHasSeenIntro: (hasSeen: boolean) => void;
    /** Imposta la lista dei profili (sincronizza con l'utente) */
    setProfili: (profili: IProfilo[]) => void;
    /** Imposta il ruolo attivo */
    setActiveRole: (role: RuoloAttivo) => void;
    /** Imposta la lista dei premi */
    setPremi: (premi: IPremio[]) => void;
    /** Pulisce la lista dei premi */
    clearPremi: () => void;
    /** Imposta l'indirizzo di spedizione */
    setIndirizzoSpedizione: (indirizzo: IndirizzoSpedizione) => void;
    /** Imposta l'indirizzo di residenza */
    setIndirizzoResidenza: (indirizzo: IndirizzoResidenza) => void;
    /** Imposta l'indirizzo di domicilio */
    setIndirizzoDomicilio: (indirizzo: IndirizzoDomicilio) => void;
    /** Pulisce tutti gli indirizzi */
    clearIndirizzi: () => void;
}

/**
 * Interfaccia che definisce le proprietà computed dello store.
 *
 * Queste proprietà sono calcolate automaticamente in base allo stato corrente
 * e forniscono informazioni derivate utili per l'applicazione.
 */
export interface IAppStoreComputed {
    /** Indica se l'utente è autenticato */
    isAuthenticated: () => boolean;
    /** Indica se è presente uno username */
    hasUsername: () => boolean;
    /** Indica se è presente un device ID */
    hasDeviceId: () => boolean;
    /** Indica se ci sono profili disponibili nell'utente */
    hasProfili: () => boolean;
    /** Indica se l'utente ha già visto l'intro */
    hasSeenIntro: () => boolean;
    /** Indica se i contatti dell'utente sono verificati */
    areContactsVerified: () => boolean;
}

/**
 * Store globale dell'applicazione basato su NgRx Signals.
 *
 * Questo store gestisce tutto lo stato dell'applicazione utilizzando NgRx Signals
 * per una gestione reattiva e performante. Include sincronizzazione automatica
 * con il localStorage per persistere i dati tra le sessioni.
 *
 * **Caratteristiche principali:**
 * - Gestione dell'autenticazione e dei token
 * - Gestione dei profili utente (integrati nell'utente) e ruoli attivi
 * - Gestione dei premi e indirizzi
 * - Sincronizzazione automatica con localStorage
 * - Proprietà computed per stati derivati
 * - Inizializzazione automatica del device ID
 *
 * **Utilizzo:**
 * ```typescript
 * constructor(private appStore: AppStore) {}
 *
 * // Lettura dello stato
 * const isAuthenticated = this.appStore.isAuthenticated();
 * const currentUser = this.appStore.user();
 *
 * // Modifica dello stato
 * this.appStore.setUser(newUser);
 * this.appStore.setToken(authToken);
 * ```
 *
 * @example
 * ```typescript
 * // Esempio di utilizzo in un componente
 * export class MyComponent {
 *   constructor(private store: AppStore) {}
 *
 *   ngOnInit() {
 *     // Controllo se l'utente è autenticato
 *     if (this.store.isAuthenticated()) {
 *       // this.store.user() contiene i dati utente
 *     }
 *   }
 *
 *   login(token: IToken, user: User) {
 *     this.store.setToken(token);
 *     this.store.setUser(user);
 *   }
 *
 *   logout() {
 *     this.store.clearAuth();
 *   }
 * }
 * ```
 */
export const AppStore = signalStore(
    { providedIn: 'root' },
    withState(initialState),
    withStorageSync({
        key: APP_STORAGE_KEY,
        select: (state: AuthStore) => ({
            token: state.token,
            user: state.user,
            username: state.username,
            device_id: state.device_id,
            authAppUrl: state.authAppUrl,
            id_profilo: state.id_profilo,
            hasSeenIntro: state.hasSeenIntro,
            activeRole: state.activeRole,
            premi: state.premi,
            indirizzoSpedizione: state.indirizzoSpedizione,
            indirizzoResidenza: state.indirizzoResidenza,
            indirizzoDomicilio: state.indirizzoDomicilio,
            is_sso: state.is_sso,
        })
    }),
    withComputed(({ token, user, username, device_id, hasSeenIntro }) => ({
        /**
         * Computed property che indica se l'utente è autenticato.
         * @returns True se sono presenti sia token che user
         */
        isAuthenticated: computed(() => Boolean(token() && user())),

        /**
         * Computed property che indica se è presente uno username.
         * @returns True se lo username è presente e non vuoto
         */
        hasUsername: computed(() => Boolean(username())),

        /**
         * Computed property che indica se è presente un device ID.
         * @returns True se il device ID è presente e non vuoto
         */
        hasDeviceId: computed(() => Boolean(device_id())),

        /**
         * Computed property che indica se ci sono profili disponibili nell'utente.
         * @returns True se l'utente ha profili e l'array ha almeno un elemento
         */
        hasProfili: computed(() => {
            const currentUser = user();
            return currentUser && currentUser.profili ? currentUser.profili.length > 0 : false;
        }),

        /**
         * Computed property che indica se i contatti dell'utente sono verificati.
         * devo ricostruire l'oggetto user perchè il localStorage non salva le classi
         * @returns True se l'utente ha contatti verificati
         */
        areContactsVerified: computed(() => {
            const raw = user();
            if (!raw) return false;
            const currentUser = raw instanceof User ? raw : new User(raw);
            return currentUser.isContattiVerificati();
        }),
    })),
    withHooks({
        /**
         * Hook di inizializzazione dello store.
         *
         * Gestisce:
         * 1. Migrazione una tantum da LEGACY_STORAGE_KEY (se presente)
         * 2. Inizializzazione del device_id se non presente dopo l'hydration di withStorageSync
         *
         * NOTA: Non legge manualmente APP_STORAGE_KEY perché withStorageSync gestisce
         * automaticamente l'hydration completa dello stato da localStorage. Questo evita
         * race conditions e duplicazioni di logica.
         */
        onInit: (store) => {
            const deviceIdService = inject(DeviceIdService);
            
            // 1. Migrazione una tantum da LEGACY_STORAGE_KEY (solo se APP_STORAGE_KEY non esiste)
            const hasAppStorageKey = localStorage.getItem(APP_STORAGE_KEY);
            if (!hasAppStorageKey) {
                const legacy = localStorage.getItem(LEGACY_STORAGE_KEY);
                if (legacy) {
                    try {
                        const parsed = JSON.parse(legacy);
                        const toPatch: Partial<AuthStore> = {
                            token: parsed.token ?? null,
                            user: parsed.user ?? null,
                            username: parsed.username ?? null,
                            device_id: parsed.device_id ?? null,
                            hasSeenIntro: parsed.hasSeenIntro ?? false,
                            activeRole: parsed.activeRole ?? null,
                            premi: Array.isArray(parsed.premi) ? parsed.premi : [],
                            indirizzoSpedizione: parsed.indirizzoSpedizione ?? null,
                            indirizzoResidenza: parsed.indirizzoResidenza ?? null,
                            indirizzoDomicilio: parsed.indirizzoDomicilio ?? null,
                            is_sso: typeof parsed.is_sso === 'boolean' ? parsed.is_sso : null,
                        };
                        patchState(store, toPatch);
                        localStorage.removeItem(LEGACY_STORAGE_KEY);
                    } catch {
                        // Migrazione fallita, rimuovi legacy e continua
                        localStorage.removeItem(LEGACY_STORAGE_KEY);
                    }
                }
            }
            
            // 2. Verifica se device_id è presente dopo l'hydration di withStorageSync
            // Se non c'è, generane uno nuovo (può succedere se è la prima volta o se i dati sono stati puliti)
            if (!store.device_id()) {
                const newDeviceId = deviceIdService.generateDeviceId();
                patchState(store, { device_id: newDeviceId });
            }
        }
    }),
    withMethods(({ ...store }) => {
        const deviceIdService = inject(DeviceIdService);
        return {
            /**
             * Imposta il token di autenticazione nello store.
             *
             * @param token Il token di autenticazione da salvare (o null per rimuoverlo)
             */
            setToken: (token: IToken | null) => {
                patchState(store, { token: token });
            },

            /**
             * Imposta i dati dell'utente nello store.
             *
             * @param user I dati dell'utente da salvare
             */
            setUser: (user: User) => {
                patchState(store, { user: user });
            },

            /**
             * Imposta lo username dell'utente nello store.
             *
             * @param username Lo username dell'utente da salvare
             */
            setUsername: (username: string) => {
                patchState(store, { username: username });
            },

            /**
             * Imposta l'ID del dispositivo nello store.
             *
             * @param deviceId L'ID del dispositivo da salvare
             */
            setDeviceId: (deviceId: string) => {
                patchState(store, { device_id: deviceId });
            },

            /**
             * Inizializza il device ID se non presente.
             * Se non c'è un device_id salvato, ne genera uno nuovo.
             */
            initializeDeviceId: () => {
                if (store.device_id()) {
                    return;
                }
                const newDeviceId = deviceIdService.generateDeviceId();
                patchState(store, { device_id: newDeviceId });
            },

            /**
             * Imposta l'URL base dell'app di autenticazione SSO.
             *
             * @param url L'URL base dell'app di autenticazione
             */
            setAuthAppUrl: (url: string) => {
                patchState(store, { authAppUrl: url });
            },

            /**
             * Restituisce l'URL Auth-App risolto.
             * Priorità: environment > store salvato.
             * Questo garantisce che eventuali cambiamenti nell'environment abbiano sempre la precedenza.
             *
             * @returns L'URL base dell'app di autenticazione
             */
            authAppUrlResolved: () => {
                const envUrl = (environment as { AUTH_APP_URL?: string }).AUTH_APP_URL;
                // Se l'environment ha un valore, usalo (ha sempre priorità)
                if (envUrl) {
                    return envUrl;
                }
                // Altrimenti usa quello salvato nello store (fallback)
                return store.authAppUrl() ?? '';
            },

            /**
             * Imposta l'ID profilo per multiutenza SSO.
             *
             * @param id L'ID del profilo da salvare (o null per rimuoverlo)
             */
            setIdProfilo: (id: number | null) => {
                patchState(store, { id_profilo: id });
            },

            /**
             * Imposta se la sessione corrente è SSO (true) o login legacy (false).
             *
             * @param value true | false | null (non autenticato)
             */
            setIsSso: (value: boolean | null) => {
                patchState(store, { is_sso: value });
            },

            /**
             * Pulisce tutti i dati di autenticazione e dello store.
             *
             * Resetta lo store al suo stato iniziale mantenendo però
             * device_id e hasSeenIntro per non perdere queste informazioni.
             * Pulisce esplicitamente anche il localStorage.
             */
            clearAuth: () => {
                // Salviamo device_id e hasSeenIntro per non perderli
                const currentDeviceId = store.device_id();
                const currentHasSeenIntro = store.hasSeenIntro();
                const hadToken = !!store.token();
                const hadUser = !!store.user();

                // Puliamo il localStorage esplicitamente
                localStorage.removeItem(APP_STORAGE_KEY);

                // Poi resettiamo lo store mantenendo device_id e hasSeenIntro
                patchState(store, {
                    token: null,
                    user: null,
                    username: null,
                    activeRole: null,
                    id_profilo: null,
                    authAppUrl: null,
                    error: null,
                    isLoading: false,
                    device_id: currentDeviceId,
                    hasSeenIntro: currentHasSeenIntro,
                    premi: [],
                    indirizzoSpedizione: null,
                    indirizzoResidenza: null,
                    indirizzoDomicilio: null,
                    is_sso: null,
                });
                
                if (environment.ENABLE_ALERTS) {
                  alert(`[app] [AppStore] clearAuth eseguito\nToken presente prima: ${hadToken ? 'SÌ' : 'NO'}\nUser presente prima: ${hadUser ? 'SÌ' : 'NO'}\nlocalStorage pulito: SÌ\nToken dopo: NO`);
                } else {
                  Console.log('AppStore - clearAuth eseguito', { hadToken, hadUser }, 'info');
                }
            },

            /**
             * Imposta lo stato di visualizzazione dell'intro.
             *
             * @param hasSeen Indica se l'utente ha già visto l'intro
             */
            setHasSeenIntro: (hasSeen: boolean) => {
                patchState(store, { hasSeenIntro: hasSeen });
            },

            /**
             * Imposta la lista dei profili (sincronizza con l'utente)
             *
             * @param profili Array dei profili da salvare
             */
            setProfili: (profili: IProfilo[]) => {
                const currentUser = store.user();
                if (currentUser) {
                    currentUser.profili = profili;
                    patchState(store, { user: currentUser });
                }
            },

            /**
             * Imposta il ruolo attivo dell'utente nello store.
             *
             * @param role Il ruolo attivo da salvare
             */
            setActiveRole: (role: RuoloAttivo) => {
                patchState(store, { activeRole: role });
            },

            /**
             * Imposta la lista dei premi disponibili nello store.
             *
             * @param premi Array dei premi da salvare
             */
            setPremi: (premi: IPremio[]) => {
                patchState(store, { premi });
            },

            /**
             * Pulisce la lista dei premi nello store.
             *
             * Resetta l'array dei premi a vuoto.
             */
            clearPremi: () => {
                patchState(store, { premi: [] });
            },

            /**
             * Imposta l'indirizzo di spedizione nello store.
             *
             * @param indirizzo L'indirizzo di spedizione da salvare
             */
            setIndirizzoSpedizione: (indirizzo: IndirizzoSpedizione) => {
                patchState(store, { indirizzoSpedizione: indirizzo });
            },

            /**
             * Imposta l'indirizzo di residenza nello store.
             *
             * @param indirizzo L'indirizzo di residenza da salvare
             */
            setIndirizzoResidenza: (indirizzo: IndirizzoResidenza) => {
                patchState(store, { indirizzoResidenza: indirizzo });
            },

            /**
             * Imposta l'indirizzo di domicilio nello store.
             *
             * @param indirizzo L'indirizzo di domicilio da salvare
             */
            setIndirizzoDomicilio: (indirizzo: IndirizzoDomicilio) => {
                patchState(store, { indirizzoDomicilio: indirizzo });
            },

            /**
             * Pulisce tutti gli indirizzi salvati nello store.
             *
             * Resetta tutti gli indirizzi (spedizione, residenza, domicilio) a null.
             */
            clearIndirizzi: () => {
                patchState(store, {
                    indirizzoSpedizione: null,
                    indirizzoResidenza: null,
                    indirizzoDomicilio: null
                });
            }
        };
    })
);

/**
 * Tipo che rappresenta l'istanza completa dell'AppStore.
 *
 * Include sia le proprietà dello stato che i metodi e le computed properties.
 * Questo tipo può essere utilizzato per tipizzare l'injection dell'AppStore.
 */
export type AppStoreType = typeof AppStore;
